import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "bg-neutral-100" }
const _hoisted_2 = { class: "container mx-auto" }
const _hoisted_3 = { class: "flex flex-col justify-center max-w-lg min-h-[calc(100vh-250px)] mx-auto text-center" }
const _hoisted_4 = {
  class: "text-base flex flex-col items-center justify-center",
  autocomplete: "off"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h1", { class: "font-extrabold text-4xl max-w-full my-8" }, "Website to IP lookup", -1)
const _hoisted_6 = { class: "mt-8 mb-20 text-center max-w-full h-20 w-[500px] text-neutral-800" }
const _hoisted_7 = { class: "prose max-w-6xl prose-indigo mt-6 mx-auto bg-white p-6 rounded-t shadow" }
const _hoisted_8 = { class: "flex flex-col justify-between md:flex-row" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("h2", { class: "inline-block text-2xl mt-0" }, "How to lookup a website's IP address", -1)
const _hoisted_10 = { class: "shrink-0 flex items-center mb-5 text-sm font-medium leading-4" }
const _hoisted_11 = /*#__PURE__*/_createElementVNode("span", { class: "hidden mr-2 sm:inline" }, "Jump to", -1)
const _hoisted_12 = {
  type: "button",
  class: "inline-flex items-center rounded-md border border-[#3a0161] bg-white px-3 py-2 text-[#3a0161] shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
}
const _hoisted_13 = { class: "first-letter:uppercase" }
const _hoisted_14 = { class: "md:grid md:gap-6 md:grid-cols-2" }
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Every website has at least one IP address connected to its domain name. For example, the website Facebook has, among others, the IP address "),
  /*#__PURE__*/_createElementVNode("code", null, "157.240.214.35"),
  /*#__PURE__*/_createTextVNode(" connected to the domain name "),
  /*#__PURE__*/_createElementVNode("code", null, "www.facebook.com"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("p", null, "When you enter a URL in your browser, it will first determine the domain name of that URL. It then looks up the IP address and connects to the server with this IP. Once connected, it fetches the website's source code to display the page.", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("img", {
  class: "w-full md:mb-0 md:mt-6",
  src: "img/pages/record-type-lookup/a-lookup-mac.png",
  alt: "Example output of an IPv4 address lookup"
}, null, -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("h2", { id: "windows" }, "How to lookup a website's IP address on Windows", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("p", null, "To check the server IP addresses for a domain name on Windows, follow these steps:", -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("em", null, "command prompt", -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("img", {
  loading: "lazy",
  class: "block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2",
  src: "img/pages/record-type-lookup/open-command-prompt-windows.png",
  alt: "Open a command prompt in Windows"
}, null, -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("code", null, "nslookup -q=A example.com", -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("code", null, "example.com", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("img", {
  loading: "lazy",
  class: "block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2",
  src: "img/pages/record-type-lookup/a-lookup-windows.png",
  alt: "IPv4 address lookup in Windows"
}, null, -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createTextVNode("The IP addresses are listed below the "),
  /*#__PURE__*/_createElementVNode("strong", null, "Non-authoritative answer"),
  /*#__PURE__*/_createTextVNode(" heading.")
], -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createTextVNode("(Optional) To also see the IPv6 addresses, repeat these steps with "),
  /*#__PURE__*/_createElementVNode("code", null, "AAAA"),
  /*#__PURE__*/_createTextVNode(" instead of "),
  /*#__PURE__*/_createElementVNode("code", null, "A"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("h2", { id: "mac" }, "How to lookup a website's IP address on Mac OS", -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("p", null, "To check the IP addresses for a domain name on a Mac, follow these steps:", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("em", null, "terminal", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("img", {
  loading: "lazy",
  class: "block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2",
  src: "img/pages/record-type-lookup/open-terminal-mac.png",
  alt: "Open a terminal on a Mac"
}, null, -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("code", null, "dig example.com A", -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("code", null, "example.com", -1)
const _hoisted_33 = /*#__PURE__*/_createElementVNode("img", {
  loading: "lazy",
  class: "block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2",
  src: "img/pages/record-type-lookup/a-lookup-mac.png",
  alt: "IPv4 lookup on a Mac"
}, null, -1)
const _hoisted_34 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createTextVNode("The IP addresses are listed below the "),
  /*#__PURE__*/_createElementVNode("strong", null, "ANSWER SECTION"),
  /*#__PURE__*/_createTextVNode(" heading.")
], -1)
const _hoisted_35 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createTextVNode("(Optional) To also see the IPv6 addresses, repeat these steps with "),
  /*#__PURE__*/_createElementVNode("code", null, "AAAA"),
  /*#__PURE__*/_createTextVNode(" instead of "),
  /*#__PURE__*/_createElementVNode("code", null, "A"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_36 = /*#__PURE__*/_createElementVNode("h2", { id: "linux" }, "How to lookup a website's IP address on Linux", -1)
const _hoisted_37 = /*#__PURE__*/_createElementVNode("p", null, "To check the IP addresses for a domain name on Linux, follow these steps:", -1)
const _hoisted_38 = /*#__PURE__*/_createElementVNode("em", null, "terminal", -1)
const _hoisted_39 = /*#__PURE__*/_createElementVNode("img", {
  loading: "lazy",
  class: "block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2",
  src: "img/pages/record-type-lookup/open-terminal-linux.png",
  alt: "Open a terminal in Linux"
}, null, -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("code", null, "dig example.com A", -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("code", null, "example.com", -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("img", {
  loading: "lazy",
  class: "block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2",
  src: "img/pages/record-type-lookup/a-lookup-linux.png",
  alt: "IPv4 lookup in Linux"
}, null, -1)
const _hoisted_43 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createTextVNode("The IP addresses are listed below the "),
  /*#__PURE__*/_createElementVNode("strong", null, "ANSWER SECTION"),
  /*#__PURE__*/_createTextVNode(" heading.")
], -1)
const _hoisted_44 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createTextVNode("(Optional) To also see the IPv6 addresses, repeat these steps with "),
  /*#__PURE__*/_createElementVNode("code", null, "AAAA"),
  /*#__PURE__*/_createTextVNode(" instead of "),
  /*#__PURE__*/_createElementVNode("code", null, "A"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_45 = /*#__PURE__*/_createElementVNode("h2", null, "How to find a website's IP address", -1)
const _hoisted_46 = /*#__PURE__*/_createElementVNode("p", null, "To check the IP addresses of a domain, follow these steps:", -1)
const _hoisted_47 = /*#__PURE__*/_createElementVNode("img", {
  loading: "lazy",
  class: "block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2",
  src: "img/pages/website-to-ip-lookup/website-to-ip-lookup.png",
  alt: "Open the website to IP lookup tool"
}, null, -1)
const _hoisted_48 = /*#__PURE__*/_createElementVNode("img", {
  loading: "lazy",
  class: "block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2",
  src: "img/pages/website-to-ip-lookup/website-to-ip-lookup-results.png",
  alt: "Example IP addresses"
}, null, -1)
const _hoisted_49 = /*#__PURE__*/_createElementVNode("li", null, "The tool will query the IPv4 and IPv6 addresses and shown them at the top of the page.", -1)

import { useRouter } from 'vue-router';
import HeaderComponent from '@/components/layout/HeaderComponent.vue';
import FooterComponent from '@/components/layout/FooterComponent.vue';
import DomainSearch from '@/components/DomainSearch.vue';
import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import Icon from '@/components/micro/IconComponent.vue';
import MetaTags from '@/components/MetaTags.vue';
import { LargeRectangle, MediumRectangle } from '@/common/adSizes';
import AdUnit from '@/components/ads/AdUnit.vue';
import { domainNameToUrlPath } from '@/common/domainName';
import { WebsiteToIpLookup } from '@/common/adSlots';


export default /*#__PURE__*/_defineComponent({
  __name: 'WebsiteToIpLookup',
  setup(__props) {

const router = useRouter();

const platforms: Array<'windows' | 'mac' | 'linux'> = [
  'windows',
  'mac',
  'linux',
];

function redirectTo(domain: string): void {
  router.push(`/domains/${domainNameToUrlPath(domain)}/webservers/`);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      _createVNode(MetaTags, {
        title: "Website to IP Lookup",
        description: "Find the IP addresses for any website or domain name. This online tool shows both IPv4 and IPv6 addresses as well as their hosting provider and location.",
        url: `https://www.nslookup.io/website-to-ip-lookup/`
      }, null, 8, ["url"])
    ])),
    _createVNode(HeaderComponent, {
      "domain-search-to-url": (encodedDomain) => `/domains/${encodedDomain}/webservers/`,
      "search-button-text": "Find IP addresses"
    }, null, 8, ["domain-search-to-url"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("main", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("form", _hoisted_4, [
            _hoisted_5,
            _createVNode(DomainSearch, {
              class: "h-12 mb-6 max-w-full w-[500px]",
              autofocus: "",
              emphasize: "",
              buttonText: "Find IP addresses",
              "placeholder-text": "URL or domain name",
              onSubmitted: redirectTo
            }),
            _createElementVNode("p", _hoisted_6, [
              _createTextVNode("Find all IP addresses with this website to IP checker. For example, try "),
              _createVNode(DnsRecordsLink, {
                page: "webservers",
                domain: "facebook.com",
                class: "text-blue-600 hover:text-blue-800 hover:underline"
              }),
              _createTextVNode(" or "),
              _createVNode(DnsRecordsLink, {
                page: "webservers",
                domain: "google.com",
                class: "text-blue-600 hover:text-blue-800 hover:underline"
              }),
              _createTextVNode(" to view their IP addresses. You can also enter a URL to get its IP addresses. ")
            ]),
            _createVNode(AdUnit, {
              class: "mt-8 mx-auto",
              config: _unref(WebsiteToIpLookup)
            }, null, 8, ["config"])
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _hoisted_9,
            _createElementVNode("span", _hoisted_10, [
              _hoisted_11,
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(platforms, (platform) => {
                return _createVNode(_component_router_link, {
                  key: platform,
                  to: `#${platform.toLowerCase()}`,
                  class: "no-underline mr-2 last:mr-0"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("button", _hoisted_12, [
                      _createVNode(Icon, {
                        icon: platform,
                        class: "mr-2"
                      }, null, 8, ["icon"]),
                      _createElementVNode("span", _hoisted_13, _toDisplayString(platform), 1)
                    ])
                  ]),
                  _: 2
                }, 1032, ["to"])
              }), 64))
            ])
          ]),
          _createElementVNode("div", _hoisted_14, [
            _createElementVNode("div", null, [
              _hoisted_15,
              _hoisted_16,
              _createElementVNode("p", null, [
                _createTextVNode("These IP addresses are configured in the Domain Name System (DNS) as "),
                _createVNode(_component_router_link, { to: "/learning/dns-record-types/a/" }, {
                  default: _withCtx(() => [
                    _createTextVNode("A")
                  ]),
                  _: 1
                }),
                _createTextVNode(" and "),
                _createVNode(_component_router_link, { to: "/learning/dns-record-types/aaaa/" }, {
                  default: _withCtx(() => [
                    _createTextVNode("AAAA")
                  ]),
                  _: 1
                }),
                _createTextVNode(" records. But how can you look up those IP addresses? That depends on the operating system you are using. Windows, Linux and Mac all have a different way of finding the IPv4 and IPv6 addresses. The sections below list the instructions for each operating system.")
              ])
            ]),
            _hoisted_17
          ]),
          _hoisted_18,
          _hoisted_19,
          _createElementVNode("ol", null, [
            _createElementVNode("li", null, [
              _createTextVNode("Open a "),
              _hoisted_20,
              _createTextVNode(" by navigating to "),
              _createElementVNode("strong", null, [
                _createVNode(Icon, { icon: "click" }),
                _createTextVNode(" Start → "),
                _createVNode(Icon, { icon: "click" }),
                _createTextVNode(" 'Type here to search' → "),
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" 'cmd' → "),
                _createVNode(Icon, { icon: "click" }),
                _createTextVNode(" Open")
              ]),
              _createTextVNode("."),
              _hoisted_21
            ]),
            _createElementVNode("li", null, [
              _createTextVNode("Type "),
              _createElementVNode("strong", null, [
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(),
                _hoisted_22
              ]),
              _createTextVNode(" and hit "),
              _createElementVNode("strong", null, [
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" [enter]")
              ]),
              _createTextVNode(" to get the IPv4 addresses for "),
              _hoisted_23,
              _createTextVNode("."),
              _hoisted_24
            ]),
            _hoisted_25,
            _hoisted_26
          ]),
          _hoisted_27,
          _hoisted_28,
          _createElementVNode("ol", null, [
            _createElementVNode("li", null, [
              _createTextVNode("Open a "),
              _hoisted_29,
              _createTextVNode(" by entering "),
              _createElementVNode("strong", null, [
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" [command] + [space] → "),
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" 'terminal.app' → "),
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" [enter]")
              ]),
              _createTextVNode("."),
              _hoisted_30
            ]),
            _createElementVNode("li", null, [
              _createTextVNode("Type "),
              _createElementVNode("strong", null, [
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(),
                _hoisted_31
              ]),
              _createTextVNode(" and hit "),
              _createElementVNode("strong", null, [
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" [enter]")
              ]),
              _createTextVNode(" to get the IPv4 addresses for "),
              _hoisted_32,
              _createTextVNode("."),
              _hoisted_33
            ]),
            _hoisted_34,
            _hoisted_35
          ]),
          _hoisted_36,
          _hoisted_37,
          _createElementVNode("ol", null, [
            _createElementVNode("li", null, [
              _createTextVNode("Open a "),
              _hoisted_38,
              _createTextVNode(" by entering "),
              _createElementVNode("strong", null, [
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" [Super] → "),
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" 'terminal' → "),
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" [enter]")
              ]),
              _createTextVNode("."),
              _hoisted_39
            ]),
            _createElementVNode("li", null, [
              _createTextVNode("Type "),
              _createElementVNode("strong", null, [
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(),
                _hoisted_40
              ]),
              _createTextVNode(" and hit "),
              _createElementVNode("strong", null, [
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" [enter]")
              ]),
              _createTextVNode(" to get the IPv4 addresses for "),
              _hoisted_41,
              _createTextVNode("."),
              _hoisted_42
            ]),
            _hoisted_43,
            _hoisted_44
          ]),
          _hoisted_45,
          _hoisted_46,
          _createElementVNode("ol", null, [
            _createElementVNode("li", null, [
              _createTextVNode("Open the "),
              _createVNode(_component_router_link, { to: "/website-to-ip-lookup/" }, {
                default: _withCtx(() => [
                  _createTextVNode("website to IP lookup tool")
                ]),
                _: 1
              }),
              _createTextVNode("."),
              _hoisted_47
            ]),
            _createElementVNode("li", null, [
              _createTextVNode("Enter the URL or domain name and hit "),
              _createElementVNode("strong", null, [
                _createVNode(Icon, { icon: "type" }),
                _createTextVNode(" [enter]")
              ]),
              _createTextVNode("."),
              _hoisted_48
            ]),
            _hoisted_49
          ])
        ])
      ])
    ]),
    _createVNode(FooterComponent)
  ], 64))
}
}

})